import BugsnagPluginReact from '@bugsnag/plugin-react';
import { clientMount } from '@vp/ubik-fragment-react';
import React from 'react';
import Bugsnag from '@bugsnag/js';
import { initialize, waitTillAvailable } from '@vp/ab-reader';
import type { MountWithRootOptions } from '@vp/ubik-fragment-types';
import { BriefsEnvironment } from '@99designs/feature-design-services-brief';
import { seedRouterFromUbikLocalContext } from '@99designs/ubik-utils';
import ubikLocalConfig from '../../ubik-local.config.json';
import { App } from '../app';
import type { Props } from '../app';

function getEnv(): BriefsEnvironment {
    if (window.location.hostname.includes('localhost')) {
        return 'DEV';
    }
    if (window.location.hostname.includes('staging')) {
        return 'STAGING';
    }
    return 'PROD';
}

export const mount: MountWithRootOptions<Props> = async (rootElement, renderProps, rootOptions) => {
    seedRouterFromUbikLocalContext(ubikLocalConfig.config.context);

    const { localeReference, locale } = renderProps;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line no-restricted-properties
    const stringsTable = await System.import(localeReference);
    const resources = {
        [locale]: {
            translation: stringsTable,
        },
    };

    // This causes the fragment to wait until we have an active ab-reader before rendering
    initialize();
    await waitTillAvailable(1000);

    if (process.env.WATCH_PORT) {
        const ws = new WebSocket(`ws://localhost:${process.env.WATCH_PORT}`);
        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.code === 'watch:end') {
                console.log('Watch End!');
                // eslint-disable-next-line no-restricted-globals
                location.reload();
            }
        };
    }

    Bugsnag.start({
        apiKey: process.env.BRIEFS_BUGSNAG_API_KEY ?? '',
        enabledReleaseStages: ['production'],
        releaseStage: process.env.BRIEFS_BUGSNAG_RELEASE_STAGE,
        autoTrackSessions: process.env.BRIEFS_BUGSNAG_RELEASE_STAGE === 'production',
        plugins: [new BugsnagPluginReact(React)],
        onError: (event) => {
            event.addMetadata('request', {
                url: event.request.url,
                headers: event.request.headers,
            });

            event.addMetadata('debug info', {
                isUbik: 'true',
                errorLocation: typeof window === 'undefined' ? 'server' : 'client',
            });
        },
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary();

    return clientMount(
        <ErrorBoundary>
            <App {...renderProps} environment={getEnv()} resources={resources} />
        </ErrorBoundary>,
        rootElement,
        rootOptions
    );
};
